import { useEffect, useState, useCallback } from "react";
import { Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { PlanStatusBadge } from "@/components/common";
import { TenantFormDialog } from "@/components/admin/TenantFormDialog";
import { TenantDetailDialog } from "@/components/admin/TenantDetailDialog";
import { TenantConfigDialog } from "@/components/admin/master/TenantConfigDialog";
import { TenantAccessDialog, MODE_SHORT } from "@/components/admin/master/TenantAccessDialog";
import { adminApi, errMsg, fmtDate, fmtDateTime, brl, STATUS_LABELS, MODULE_LABELS } from "@/lib/api";
import { toast } from "sonner";

// Primeira contratação com cadastro incompleto: o Admin Master decide (só avisa e pede confirmação, não bloqueia)
const avisoCadastro = (cad, nome) => (cad?.exigido
  ? window.confirm(`O cadastro de ${nome} está incompleto (faltam: ${cad.faltando.join(", ")}).\n\nNa primeira contratação o sistema pede esses dados ao cliente. Ativar o plano mesmo assim?`)
  : true);

function PendingRequests({ onApprove, tick }) {
  const [items, setItems] = useState([]);
  useEffect(() => { adminApi.get("/subscription-requests").then((r) => setItems(r.data.filter((x) => x.status === "pending"))).catch(() => {}); }, [tick]);
  if (!items.length) return null;
  return (
    <div className="card-surface p-5" data-testid="admin-pending-requests">
      <p className="eyebrow mb-3">Solicitações de assinatura pendentes</p>
      <ul className="divide-y text-sm">
        {items.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-3 py-2">
            <span className="flex-1">{r.nome_fantasia} <span className="font-mono text-xs text-muted-foreground">({r.access_number})</span> · plano <b>{r.plan_name}</b> {brl(r.price)} · {fmtDateTime(r.created_at)}
              {r.cadastro?.exigido && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-testid={`request-incomplete-${r.id}`}>Cadastro incompleto: {r.cadastro.faltando.join(", ")}</span>}</span>
            <Button size="sm" onClick={() => avisoCadastro(r.cadastro, r.nome_fantasia) && onApprove(r.tenant_id, { status: "active", plan: r.plan_id })} data-testid={`approve-request-${r.id}`}>Confirmar assinatura</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

const EXTRA_MODULES = ["fiscal", "folha"];

// Cadastro rápido: e-mail ou WhatsApp já usados em outra empresa (não bloqueia, só avisa para conferir)
function RepeatBadge({ t }) {
  const a = t.alerta_repeticao;
  if (!a || (!a.email && !a.telefone)) return null;
  const o = [a.email && "e-mail", a.telefone && "WhatsApp"].filter(Boolean).join(" e ");
  return <span className="mt-1 inline-block rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400" title={`O ${o} deste cadastro já existe em outra empresa. Confira se não é a mesma pessoa repetindo o teste grátis.`} data-testid={`admin-repeat-${t.access_number}`}>Possível repetição ({o})</span>;
}

// Cadastro interrompido cuja limpeza não terminou: precisa de intervenção (só registros da empresa que estava sendo criada)
function CleanupWarning({ info }) {
  const l = info?.limpezas_pendentes || [];
  if (!l.length) return null;
  return (
    <div className="card-surface border-red-500/40 p-4 text-sm" data-testid="admin-cleanup-warning">
      <p className="font-semibold text-red-600 dark:text-red-400">Cadastro interrompido com limpeza pendente</p>
      <p className="mt-1 text-muted-foreground">Um cadastro falhou no meio e não foi possível apagar tudo o que ele gravou. Os registros abaixo são só da empresa que estava sendo criada (ela fica bloqueada). Avise o suporte técnico para concluir a limpeza.</p>
      <ul className="mt-2 space-y-1" data-testid="admin-cleanup-list">{l.map((x) => <li key={x.tenant_id}><span className="font-mono">{x.tenant_id}</span>: falta apagar {x.pendentes.join(", ")}{x.motivo ? ` (${x.motivo})` : ""}</li>)}</ul>
    </div>
  );
}

// CPF/CNPJ repetidos já gravados: o índice único não é criado até o Admin resolver (nada é alterado sozinho)
function DocsWarning({ info }) {
  if (!info || (info.indice_ativo && !info.repetidos?.length)) return null;
  return (
    <div className="card-surface border-amber-500/40 p-4 text-sm" data-testid="admin-docs-warning">
      <p className="font-semibold text-amber-600 dark:text-amber-400">Proteção de CPF/CNPJ único {info.indice_ativo ? "ativa, mas há documentos repetidos" : "ainda não ativada"}</p>
      {info.repetidos?.length > 0 ? (<>
        <p className="mt-1 text-muted-foreground">Estes documentos estão em mais de uma empresa. Nada foi alterado. Corrija o documento de uma delas (Ver detalhes → CPF/CNPJ) e reinicie o sistema para ativar a proteção.</p>
        <ul className="mt-2 space-y-1" data-testid="admin-docs-repeated">{info.repetidos.map((r) => <li key={r.documento}><span className="font-mono">{r.documento}</span>: {r.empresas.map((e) => `${e.nome_fantasia} (${e.access_number})`).join(", ")}</li>)}</ul>
      </>) : <p className="mt-1 text-muted-foreground">Sem documentos repetidos: a proteção é ativada no próximo reinício do sistema. Enquanto isso, o cadastro continua conferindo o documento.</p>}
    </div>
  );
}

function ModulesCell({ mods, status, testId }) {
  if (mods == null) return <span className="text-xs text-muted-foreground" data-testid={testId}>{status === "trial" ? "Todos (teste)" : "Nenhum"}</span>;
  const extras = EXTRA_MODULES.filter((m) => mods.includes(m));
  const outros = mods.length - extras.length;
  return (
    <div className="flex max-w-[220px] flex-wrap items-center gap-1" title={mods.map((m) => MODULE_LABELS[m] || m).join(", ")} data-testid={testId}>
      {extras.map((m) => <span key={m} className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{m === "fiscal" ? "Fiscal" : "Folha"}</span>)}
      <span className="text-xs text-muted-foreground">{extras.length ? `+ ${outros}` : outros} módulo{outros === 1 ? "" : "s"}</span>
    </div>
  );
}

function AccessBadge({ t }) {
  if (t.blocked) return <span className="whitespace-nowrap rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-500" data-testid={`admin-access-${t.access_number}`}>Bloqueado{t.block_mode === "login" ? " (só login)" : ""}</span>;
  return (
    <span className="flex flex-col items-start gap-0.5">
      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400" data-testid={`admin-access-${t.access_number}`}>Ativo</span>
      {t.block_schedule && <span className="whitespace-nowrap text-[11px] text-amber-600 dark:text-amber-400" data-testid={`admin-schedule-${t.access_number}`}>bloqueio em {fmtDateTime(t.block_schedule.at)} ({MODE_SHORT[t.block_schedule.mode]})</span>}
    </span>
  );
}

function Actions({ t, plans, patch, onEdit, onDetail, onConfig, onAccess }) {
  const blocked = ["suspended", "cancelled"].includes(t.status);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid={`admin-actions-${t.access_number}`}>Ações</Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onDetail(t.id)} data-testid={`admin-detail-${t.access_number}`}>Ver detalhes</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(t)} data-testid={`admin-edit-${t.access_number}`}>Editar dados</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onConfig(t.id)} data-testid={`admin-config-${t.access_number}`}>Módulos e valores</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAccess(t)} className={t.blocked ? "text-emerald-600" : "text-red-500"} data-testid={`admin-access-action-${t.access_number}`}>{t.blocked ? "Liberar ou alterar bloqueio" : "Bloquear ou agendar bloqueio"}</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs">Ativar plano</DropdownMenuLabel>
        {t.cadastro?.exigido && <DropdownMenuLabel className="text-[11px] font-normal text-amber-600 dark:text-amber-400" data-testid={`admin-incomplete-${t.access_number}`}>Cadastro incompleto: {t.cadastro.faltando.join(", ")}</DropdownMenuLabel>}
        {plans.map((p) => <DropdownMenuItem key={p.id} onClick={() => avisoCadastro(t.cadastro, t.nome_fantasia) && patch(t.id, { status: "active", plan: p.id })} data-testid={`admin-activate-${p.id}-${t.access_number}`}>{p.name}</DropdownMenuItem>)}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => patch(t.id, { extend_days: 15 })} data-testid={`admin-extend-${t.access_number}`}>Estender teste +15 dias</DropdownMenuItem>
        <DropdownMenuItem onClick={() => patch(t.id, { status: "trial" })}>Voltar para teste</DropdownMenuItem>
        {blocked
          ? <DropdownMenuItem onClick={() => patch(t.id, { reactivate: true })} data-testid={`admin-reactivate-${t.access_number}`}>Reativar conta</DropdownMenuItem>
          : <DropdownMenuItem onClick={() => window.confirm(`Suspender ${t.nome_fantasia}?`) && patch(t.id, { status: "suspended" })} data-testid={`admin-suspend-${t.access_number}`}>Suspender conta</DropdownMenuItem>}
        <DropdownMenuItem onClick={() => window.confirm(`Cancelar a conta de ${t.nome_fantasia}?`) && patch(t.id, { status: "cancelled" })} className="text-red-500">Cancelar conta</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TenantsTab({ tick, onChanged }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ open: false, tenant: null });
  const [detail, setDetail] = useState(null);
  const [config, setConfig] = useState(null);
  const [access, setAccess] = useState(null);
  const load = useCallback(() => adminApi.get("/tenants", { params: { q, status, page } }).then((r) => setData(r.data)).catch(() => {}), [q, status, page]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load, tick]);
  useEffect(() => { adminApi.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); }, [tick]);
  const patch = async (id, body) => {
    try {
      await adminApi.patch(`/tenants/${id}`, body);
      toast.success("Cliente atualizado");
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <PendingRequests onApprove={patch} tick={tick} />
      <CleanupWarning info={data.documentos} />
      <DocsWarning info={data.documentos} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar por nome, e-mail, CNPJ ou número de acesso" className="pl-9 h-11" data-testid="admin-search-input" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-44 h-11" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos os status</SelectItem>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}<SelectItem value="blocked">Acesso bloqueado</SelectItem></SelectContent>
        </Select>
        <Button className="h-11" onClick={() => setForm({ open: true, tenant: null })} data-testid="admin-new-tenant-btn"><Plus className="h-4 w-4 mr-1" />Novo cliente</Button>
      </div>
      <div className="card-surface overflow-x-auto">
        <Table data-testid="admin-tenants-table">
          <TableHeader><TableRow><TableHead>Estabelecimento</TableHead><TableHead>Nº de acesso</TableHead><TableHead>Situação</TableHead><TableHead>Status / plano</TableHead><TableHead>Teste até</TableHead><TableHead>Módulos ativos</TableHead><TableHead>Usuários</TableHead><TableHead>Vendas</TableHead><TableHead>Faturamento</TableHead><TableHead>Bloquear após teste</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {data.items.map((t) => (
              <TableRow key={t.id} data-testid={`admin-tenant-row-${t.access_number}`}>
                <TableCell><button className="text-left" onClick={() => setDetail(t.id)}><p className="font-medium hover:text-primary">{t.nome_fantasia}</p><p className="text-xs text-muted-foreground">{t.email}</p></button><RepeatBadge t={t} /></TableCell>
                <TableCell className="font-mono">{t.access_number}</TableCell>
                <TableCell><AccessBadge t={t} /></TableCell>
                <TableCell><PlanStatusBadge status={t.subscription.status} testId={`admin-status-${t.access_number}`} />{t.plan && <span className="ml-2 text-xs capitalize">{t.plan}</span>}</TableCell>
                <TableCell className="whitespace-nowrap">{fmtDate(t.subscription.trial_end)} <span className="text-xs text-muted-foreground">({t.subscription.days_left}d)</span></TableCell>
                <TableCell><ModulesCell mods={t.active_modules} status={t.subscription.status} testId={`admin-modules-${t.access_number}`} /></TableCell>
                <TableCell>{t.users}</TableCell>
                <TableCell>{t.sales}</TableCell>
                <TableCell className="font-mono whitespace-nowrap">{brl(t.revenue)}</TableCell>
                <TableCell><Switch checked={t.settings.block_sales_after_trial} onCheckedChange={(v) => patch(t.id, { block_sales_after_trial: v })} data-testid={`admin-block-switch-${t.access_number}`} /></TableCell>
                <TableCell><Actions t={t} plans={plans} patch={patch} onEdit={(x) => setForm({ open: true, tenant: x })} onDetail={setDetail} onConfig={setConfig} onAccess={setAccess} /></TableCell>
              </TableRow>
            ))}
            {!data.items.length && <TableRow><TableCell colSpan={11} className="py-10 text-center text-muted-foreground">Nenhum cliente encontrado.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between text-sm" data-testid="admin-pagination">
        <span className="text-muted-foreground">{data.total} cliente(s)</span>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span>{page} / {data.pages}</span>
          <Button size="icon" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="admin-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <TenantFormDialog open={form.open} tenant={form.tenant} onOpenChange={(v) => setForm((x) => ({ ...x, open: v }))} onSaved={() => { load(); onChanged(); }} />
      <TenantDetailDialog tenantId={detail} onOpenChange={() => setDetail(null)} onChanged={load} />
      <TenantConfigDialog tenantId={config} onOpenChange={() => setConfig(null)} onSaved={load} />
      <TenantAccessDialog tenant={access} onOpenChange={() => setAccess(null)} onSaved={() => { load(); onChanged(); }} />
    </div>
  );
}
